// Thin wrapper around the dataLayer that GTM listens to.
// Every conversion-relevant interaction flows through trackEvent().
// Event names follow GA4 / GTM conventions: snake_case, verbs for actions.

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

type EventName =
  | "page_view"
  | "book_click"           // any primary Prenota CTA
  | "phone_click"          // tap-to-call
  | "whatsapp_click"       // WhatsApp deep link
  | "email_click"
  | "instagram_click"
  | "treatwell_click"      // outbound to Treatwell reviews
  | "treatment_view"       // tab / filter on treatments page
  | "nav_click"            // top-nav or bottom-tab navigation
  | "map_interact"
  | "ritual_wizard_step"   // booking page configurator steps
  | "gift_card_step"       // gift card configurator steps
  | "gift_card_pay"        // tap on SumUp checkout from gift card page
  | "booking_step"         // online-booking wizard funnel steps
  | "booking_checkout_started" // tap on SumUp pay from booking wizard
  | "booking_paid"         // thank-you page confirms a paid booking
  | "scroll_depth";

type EventPayload = Record<string, string | number | boolean | undefined>;

function push(event: string, payload: EventPayload = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });
}

export function trackEvent(name: EventName, payload: EventPayload = {}): void {
  push(name, payload);
}

export function trackPageView(path: string, title?: string): void {
  push("page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

// Standardised contact-conversion helpers — call from onClick handlers.
export const conversions = {
  book: (location: string, treatment?: string) =>
    trackEvent("book_click", { cta_location: location, treatment }),
  phone: (location: string) =>
    trackEvent("phone_click", { cta_location: location }),
  whatsapp: (location: string, treatment?: string) =>
    trackEvent("whatsapp_click", { cta_location: location, treatment }),
  email: (location: string) =>
    trackEvent("email_click", { cta_location: location }),
  instagram: (location: string) =>
    trackEvent("instagram_click", { cta_location: location }),
  treatwell: () => trackEvent("treatwell_click"),
  giftCardPay: (treatment: string, amount: number) =>
    trackEvent("gift_card_pay", { treatment, amount }),
  bookingStep: (step: string, service?: string) =>
    trackEvent("booking_step", { step, service }),
  bookingCheckoutStarted: (service: string, amount: number) =>
    trackEvent("booking_checkout_started", { service, amount }),
  bookingPaid: (reference: string, service?: string, amount?: number) =>
    trackEvent("booking_paid", { reference, service, amount }),
};

export const WHATSAPP_PHONE = "393492468186"; // e.164 without '+'
export const PHONE_HUMAN = "+39 349 246 8186";
export const PHONE_TEL = "+393492468186";
export const INSTAGRAM_URL = "https://www.instagram.com/laviens.io";
export const INSTAGRAM_HANDLE = "@laviens.io";

export function whatsappUrl(message?: string): string {
  const text = message ?? "Ciao Laviens, vorrei informazioni per prenotare un trattamento.";
  return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(text)}`;
}

export const WHATSAPP_URL = whatsappUrl();
